import { createContext, useMemo, useContext, useEffect, useState } from 'react';
import { ThemeProvider } from '@agentnative/ui';
import { create } from 'zustand';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';

var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, key + "" , value);

// src/adapter/EnvironmentDetector.ts
function detectEnvironment() {
  if (typeof process !== "undefined" && process.env) {
    const envMode = process.env.APP_MODE;
    if (envMode === "marketplace" || envMode === "standalone") {
      return createEnvironment(envMode);
    }
  }
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    if (hostname.includes("marketplace.agentnative.com")) {
      return createEnvironment("marketplace");
    }
    if (window.__AGENTNATIVE_MARKETPLACE__) {
      return createEnvironment("hybrid");
    }
  }
  return createEnvironment("standalone");
}
function createEnvironment(mode) {
  const isMarketplace = mode === "marketplace" || mode === "hybrid";
  return {
    mode,
    isMarketplace,
    isStandalone: mode === "standalone",
    capabilities: {
      chat: isMarketplace,
      notifications: isMarketplace,
      appSwitcher: isMarketplace,
      sharedSession: isMarketplace
    }
  };
}
var useNotificationStore = create((set) => ({
  notifications: [],
  add: (notification) => {
    const id = `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newNotification = {
      ...notification,
      id,
      timestamp: /* @__PURE__ */ new Date()
    };
    set((state) => ({
      notifications: [...state.notifications, newNotification]
    }));
    if (notification.duration !== 0) {
      setTimeout(() => {
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id)
        }));
      }, notification.duration || 5e3);
    }
    return id;
  },
  remove: (id) => set((state) => ({
    notifications: state.notifications.filter((n) => n.id !== id)
  })),
  clear: () => set({ notifications: [] })
}));
var NotificationService = class {
  constructor() {
    __publicField(this, "store", useNotificationStore);
  }
  /**
   * Show a notification
   */
  show(notification) {
    return this.store.getState().add(notification);
  }
  /**
   * Show a success notification
   */
  showSuccess(message, title) {
    return this.show({
      type: "success",
      title: title || "Success",
      message,
      duration: 5e3
    });
  }
  /**
   * Show an error notification
   */
  showError(message, title) {
    return this.show({
      type: "error",
      title: title || "Error",
      message,
      duration: 7e3
      // Longer for errors
    });
  }
  /**
   * Show a warning notification
   */
  showWarning(message, title) {
    return this.show({
      type: "warning",
      title: title || "Warning",
      message,
      duration: 6e3
    });
  }
  /**
   * Show an info notification
   */
  showInfo(message, title) {
    return this.show({
      type: "info",
      title: title || "Info",
      message,
      duration: 5e3
    });
  }
  /**
   * Remove a notification by ID
   */
  remove(id) {
    this.store.getState().remove(id);
  }
  /**
   * Clear all notifications
   */
  clear() {
    this.store.getState().clear();
  }
  /**
   * Get all notifications
   */
  getAll() {
    return this.store.getState().notifications;
  }
  /**
   * Subscribe to notification changes
   */
  subscribe(callback) {
    return this.store.subscribe((state) => callback(state.notifications));
  }
};
var notificationService = new NotificationService();
var useNavigationStore = create((set, get) => ({
  currentPath: typeof window !== "undefined" ? window.location.pathname : "/",
  history: [],
  setPath: (path, appId) => {
    const currentPath = get().currentPath;
    set((state) => ({
      currentPath: path,
      history: [
        ...state.history,
        {
          path: currentPath,
          timestamp: /* @__PURE__ */ new Date(),
          appId
        }
      ]
    }));
  },
  goBack: () => {
    const history = get().history;
    if (history.length > 0) {
      const previous = history[history.length - 1];
      set({
        currentPath: previous.path,
        history: history.slice(0, -1)
      });
    }
  },
  canGoBack: () => get().history.length > 0
}));
var NavigationService = class {
  constructor() {
    __publicField(this, "store", useNavigationStore);
  }
  /**
   * Navigate to a path
   */
  navigateTo(path) {
    this.store.getState().setPath(path);
    if (typeof window !== "undefined") {
      window.history.pushState({}, "", path);
      window.dispatchEvent(new CustomEvent("navigation", { detail: { path } }));
    }
  }
  /**
   * Navigate to an app
   */
  navigateToApp(appId, path) {
    const fullPath = path ? `/apps/${appId}${path}` : `/apps/${appId}`;
    this.navigateTo(fullPath);
    this.store.getState().setPath(fullPath, appId);
  }
  /**
   * Go back in history
   */
  goBack() {
    if (this.canGoBack()) {
      this.store.getState().goBack();
      if (typeof window !== "undefined") {
        window.history.back();
      }
    }
  }
  /**
   * Check if can go back
   */
  canGoBack() {
    return this.store.getState().canGoBack();
  }
  /**
   * Get current path
   */
  getCurrentPath() {
    return this.store.getState().currentPath;
  }
  /**
   * Get navigation history
   */
  getHistory() {
    return this.store.getState().history;
  }
  /**
   * Replace current path (no history entry)
   */
  replace(path) {
    this.store.setState({ currentPath: path });
    if (typeof window !== "undefined") {
      window.history.replaceState({}, "", path);
    }
  }
  /**
   * Subscribe to navigation changes
   */
  subscribe(callback) {
    return this.store.subscribe((state) => callback(state.currentPath));
  }
};
var navigationService = new NavigationService();
var useChatStore = create((set, _get) => ({
  sessions: {},
  activeSessionId: null,
  createSession: (agentId, agentName) => {
    const sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const now = /* @__PURE__ */ new Date();
    const newSession = {
      id: sessionId,
      agentId,
      agentName: agentName || (agentId ? `Agent ${agentId}` : "Support"),
      messages: [],
      isOpen: true,
      createdAt: now,
      updatedAt: now
    };
    set((state) => ({
      sessions: {
        ...state.sessions,
        [sessionId]: newSession
      },
      activeSessionId: sessionId
    }));
    return sessionId;
  },
  closeSession: (sessionId) => {
    set((state) => ({
      sessions: {
        ...state.sessions,
        [sessionId]: {
          ...state.sessions[sessionId],
          isOpen: false,
          updatedAt: /* @__PURE__ */ new Date()
        }
      },
      activeSessionId: state.activeSessionId === sessionId ? null : state.activeSessionId
    }));
  },
  openSession: (sessionId) => {
    set((state) => ({
      sessions: {
        ...state.sessions,
        [sessionId]: {
          ...state.sessions[sessionId],
          isOpen: true,
          updatedAt: /* @__PURE__ */ new Date()
        }
      },
      activeSessionId: sessionId
    }));
  },
  sendMessage: (content, sessionId, agentId) => {
    const messageId = `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const now = /* @__PURE__ */ new Date();
    const message = {
      id: messageId,
      content,
      sender: {
        id: "current-user",
        name: "You",
        type: "user"
      },
      timestamp: now,
      sessionId,
      agentId
    };
    set((state) => {
      const session = state.sessions[sessionId];
      if (!session) return state;
      return {
        sessions: {
          ...state.sessions,
          [sessionId]: {
            ...session,
            messages: [...session.messages, message],
            updatedAt: now
          }
        }
      };
    });
  },
  setActiveSession: (sessionId) => {
    set({ activeSessionId: sessionId });
  }
}));
var ChatService = class {
  constructor() {
    __publicField(this, "store", useChatStore);
  }
  /**
   * Open a new chat session
   */
  open(agentId) {
    const { sessions } = this.store.getState();
    if (agentId) {
      const existingSession = Object.values(sessions).find(
        (s) => s.agentId === agentId && s.isOpen
      );
      if (existingSession) {
        this.store.getState().setActiveSession(existingSession.id);
        return;
      }
    }
    this.store.getState().createSession(agentId);
  }
  /**
   * Close the chat
   */
  close() {
    const { activeSessionId } = this.store.getState();
    if (activeSessionId) {
      this.store.getState().closeSession(activeSessionId);
    }
  }
  /**
   * Send a message
   */
  sendMessage(message, agentId) {
    let { activeSessionId } = this.store.getState();
    if (!activeSessionId) {
      activeSessionId = this.store.getState().createSession(agentId);
    }
    this.store.getState().sendMessage(message, activeSessionId, agentId);
  }
  /**
   * Get active session
   */
  getActiveSession() {
    const { sessions, activeSessionId } = this.store.getState();
    return activeSessionId ? sessions[activeSessionId] || null : null;
  }
  /**
   * Get all sessions
   */
  getAllSessions() {
    const { sessions } = this.store.getState();
    return Object.values(sessions).sort(
      (a, b) => b.updatedAt.getTime() - a.updatedAt.getTime()
    );
  }
  /**
   * Get session by ID
   */
  getSession(sessionId) {
    return this.store.getState().sessions[sessionId] || null;
  }
  /**
   * Subscribe to chat changes
   */
  subscribe(callback) {
    return this.store.subscribe((state) => {
      const activeSession = state.activeSessionId ? state.sessions[state.activeSessionId] || null : null;
      callback(activeSession);
    });
  }
};
var chatService = new ChatService();
var useAnalyticsStore = create((set, get) => ({
  events: [],
  sessionId: `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  userId: void 0,
  addEvent: (event) => {
    const newEvent = {
      ...event,
      id: `event-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: /* @__PURE__ */ new Date(),
      sessionId: get().sessionId,
      userId: get().userId
    };
    set((state) => ({
      events: [...state.events, newEvent]
    }));
    if (process.env.NODE_ENV === "production") ;
    console.log("[Analytics]", newEvent.type, newEvent.name, newEvent.properties);
  },
  setUserId: (userId) => {
    set({ userId });
  },
  clearEvents: () => {
    set({ events: [] });
  }
}));
var AnalyticsService = class {
  constructor() {
    __publicField(this, "store", useAnalyticsStore);
  }
  /**
   * Track an event
   */
  track(event, properties) {
    this.store.getState().addEvent({
      type: "track",
      name: event,
      properties: properties || {}
    });
  }
  /**
   * Track a page view
   */
  page(name, properties) {
    this.store.getState().addEvent({
      type: "page",
      name,
      properties: {
        path: typeof window !== "undefined" ? window.location.pathname : "/",
        ...properties
      }
    });
  }
  /**
   * Identify a user
   */
  identify(userId, traits) {
    this.store.getState().setUserId(userId);
    this.track("User Identified", {
      userId,
      ...traits
    });
  }
  /**
   * Get all events
   */
  getEvents() {
    return this.store.getState().events;
  }
  /**
   * Get events by type
   */
  getEventsByType(type) {
    return this.store.getState().events.filter((e) => e.type === type);
  }
  /**
   * Get session ID
   */
  getSessionId() {
    return this.store.getState().sessionId;
  }
  /**
   * Get user ID
   */
  getUserId() {
    return this.store.getState().userId;
  }
  /**
   * Clear all events
   */
  clearEvents() {
    this.store.getState().clearEvents();
  }
  /**
   * Subscribe to analytics changes
   */
  subscribe(callback) {
    return this.store.subscribe((state) => callback(state.events));
  }
};
var analyticsService = new AnalyticsService();
var ShellContext = createContext(null);
function ShellProvider({ children, initialServices }) {
  const environment = useMemo(() => detectEnvironment(), []);
  const services = useMemo(
    () => ({
      navigation: {
        navigateTo: (path) => navigationService.navigateTo(path),
        navigateToApp: (appId, path) => navigationService.navigateToApp(appId, path),
        goBack: () => navigationService.goBack(),
        getCurrentPath: () => navigationService.getCurrentPath()
      },
      notifications: {
        show: (notification) => notificationService.show(notification),
        showSuccess: (message) => notificationService.showSuccess(message),
        showError: (message) => notificationService.showError(message),
        showWarning: (message) => notificationService.showWarning(message),
        showInfo: (message) => notificationService.showInfo(message)
      },
      chat: {
        open: (agentId) => chatService.open(agentId),
        close: () => chatService.close(),
        sendMessage: (message, agentId) => chatService.sendMessage(message, agentId)
      },
      analytics: {
        track: (event, properties) => analyticsService.track(event, properties),
        page: (name, properties) => analyticsService.page(name, properties)
      },
      ...initialServices
    }),
    [environment, initialServices]
  );
  const contextValue = useMemo(
    () => ({
      environment,
      availableApps: [],
      services,
      theme: {},
      registerApp: (config) => {
        console.log("Register app:", config.metadata.appId);
      },
      unregisterApp: (appId) => {
        console.log("Unregister app:", appId);
      }
    }),
    [environment, services]
  );
  return /* @__PURE__ */ jsx(ShellContext.Provider, { value: contextValue, children });
}
function useShellContext() {
  const context = useContext(ShellContext);
  if (!context) {
    throw new Error("useShellContext must be used within a ShellProvider");
  }
  return context;
}
function useEnvironment() {
  const { environment } = useShellContext();
  return environment;
}
function useShellServices() {
  const { services } = useShellContext();
  return services;
}
var ShellAdapter = class {
  static wrap(AppComponent, config) {
    return function WrappedApp(props) {
      const context = useShellContext();
      const environment = useEnvironment();
      useEffect(() => {
        if (environment.isMarketplace) {
          context.registerApp(config);
        }
        config.lifecycle?.onMount?.();
        return () => {
          config.lifecycle?.onUnmount?.();
          if (environment.isMarketplace) {
            context.unregisterApp(config.metadata.appId);
          }
        };
      }, []);
      return /* @__PURE__ */ jsx(ThemeProvider, { children: /* @__PURE__ */ jsx(AppComponent, { ...props }) });
    };
  }
};
function AppHeader({
  currentApp,
  availableApps = [],
  user,
  enableSearch = true,
  enableNotifications = true,
  enableAppSwitcher = true,
  logo,
  onAppSwitch: _onAppSwitch,
  onLogout,
  onSearch
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const handleSearch = (e) => {
    e.preventDefault();
    onSearch?.(searchQuery);
  };
  return /* @__PURE__ */ jsxs("header", { className: "h-16 border-b border-[var(--color-neutral-200)] bg-white px-4 flex items-center justify-between", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-4", children: [
      logo && /* @__PURE__ */ jsx("div", { className: "flex items-center", children: typeof logo === "string" ? /* @__PURE__ */ jsx("img", { src: logo, alt: "Logo", className: "h-8" }) : logo }),
      currentApp && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx("div", { className: "h-6 w-px bg-[var(--color-neutral-300)]" }),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          typeof currentApp.icon === "string" ? /* @__PURE__ */ jsx("span", { className: "text-2xl", children: currentApp.icon }) : currentApp.icon,
          /* @__PURE__ */ jsx("span", { className: "font-semibold text-[var(--color-neutral-900)]", children: currentApp.name })
        ] })
      ] })
    ] }),
    enableSearch && /* @__PURE__ */ jsx("div", { className: "flex-1 max-w-2xl mx-8", children: /* @__PURE__ */ jsx("form", { onSubmit: handleSearch, children: /* @__PURE__ */ jsxs("div", { className: "relative", children: [
      /* @__PURE__ */ jsx(
        "input",
        {
          type: "text",
          placeholder: "Search...",
          value: searchQuery,
          onChange: (e) => setSearchQuery(e.target.value),
          className: "w-full px-4 py-2 pl-10 rounded-[var(--radius-lg)] border border-[var(--color-neutral-300)] focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-primary)] focus:border-transparent"
        }
      ),
      /* @__PURE__ */ jsx(
        "svg",
        {
          className: "absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-neutral-400)]",
          fill: "none",
          stroke: "currentColor",
          viewBox: "0 0 24 24",
          children: /* @__PURE__ */ jsx(
            "path",
            {
              strokeLinecap: "round",
              strokeLinejoin: "round",
              strokeWidth: 2,
              d: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            }
          )
        }
      )
    ] }) }) }),
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
      enableAppSwitcher && availableApps.length > 0 && /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          className: "p-2 rounded-[var(--radius-md)] hover:bg-[var(--color-neutral-100)] transition-colors",
          "aria-label": "Switch app",
          children: /* @__PURE__ */ jsx(
            "svg",
            {
              className: "w-5 h-5 text-[var(--color-neutral-600)]",
              fill: "none",
              stroke: "currentColor",
              viewBox: "0 0 24 24",
              children: /* @__PURE__ */ jsx(
                "path",
                {
                  strokeLinecap: "round",
                  strokeLinejoin: "round",
                  strokeWidth: 2,
                  d: "M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                }
              )
            }
          )
        }
      ),
      enableNotifications && /* @__PURE__ */ jsxs(
        "button",
        {
          type: "button",
          className: "relative p-2 rounded-[var(--radius-md)] hover:bg-[var(--color-neutral-100)] transition-colors",
          onClick: () => setShowNotifications(!showNotifications),
          "aria-label": "Notifications",
          children: [
            /* @__PURE__ */ jsx(
              "svg",
              {
                className: "w-5 h-5 text-[var(--color-neutral-600)]",
                fill: "none",
                stroke: "currentColor",
                viewBox: "0 0 24 24",
                children: /* @__PURE__ */ jsx(
                  "path",
                  {
                    strokeLinecap: "round",
                    strokeLinejoin: "round",
                    strokeWidth: 2,
                    d: "M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                  }
                )
              }
            ),
            /* @__PURE__ */ jsx("span", { className: "absolute top-1 right-1 h-2 w-2 rounded-full bg-[var(--color-brand-danger)]" })
          ]
        }
      ),
      user && /* @__PURE__ */ jsxs("div", { className: "relative", children: [
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            className: "flex items-center gap-2 p-2 rounded-[var(--radius-md)] hover:bg-[var(--color-neutral-100)] transition-colors",
            onClick: () => setShowUserMenu(!showUserMenu),
            children: user.avatar ? /* @__PURE__ */ jsx(
              "img",
              {
                src: user.avatar,
                alt: user.name,
                className: "w-8 h-8 rounded-full"
              }
            ) : /* @__PURE__ */ jsx("div", { className: "w-8 h-8 rounded-full bg-[var(--color-brand-primary)] text-white flex items-center justify-center font-medium", children: user.name.charAt(0).toUpperCase() })
          }
        ),
        showUserMenu && /* @__PURE__ */ jsxs("div", { className: "absolute right-0 mt-2 w-48 bg-white rounded-[var(--radius-lg)] shadow-[var(--shadow-lg)] border border-[var(--color-neutral-200)] py-2 z-50", children: [
          /* @__PURE__ */ jsxs("div", { className: "px-4 py-2 border-b border-[var(--color-neutral-200)]", children: [
            /* @__PURE__ */ jsx("p", { className: "font-medium text-sm text-[var(--color-neutral-900)]", children: user.name }),
            /* @__PURE__ */ jsx("p", { className: "text-xs text-[var(--color-neutral-600)]", children: user.email })
          ] }),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              className: "w-full px-4 py-2 text-left text-sm text-[var(--color-neutral-700)] hover:bg-[var(--color-neutral-100)] transition-colors",
              onClick: onLogout,
              children: "Sign out"
            }
          )
        ] })
      ] })
    ] })
  ] });
}
AppHeader.displayName = "AppHeader";
function SidebarNavItem({
  item,
  isActive,
  onNavigate,
  level = 0
}) {
  const hasChildren = item.children && item.children.length > 0;
  return /* @__PURE__ */ jsxs("div", { children: [
    /* @__PURE__ */ jsxs(
      "button",
      {
        type: "button",
        onClick: () => onNavigate?.(item.path),
        className: `
          w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium rounded-[var(--radius-md)] transition-colors
          ${isActive ? "bg-[var(--color-brand-primary)] text-white" : "text-[var(--color-neutral-700)] hover:bg-[var(--color-neutral-100)]"}
        `,
        style: { paddingLeft: `${16 + level * 16}px` },
        children: [
          item.icon && /* @__PURE__ */ jsx("span", { className: "flex-shrink-0", children: typeof item.icon === "string" ? item.icon : item.icon }),
          /* @__PURE__ */ jsx("span", { className: "flex-1 text-left", children: item.label }),
          item.badge && /* @__PURE__ */ jsx("span", { className: "flex-shrink-0 px-2 py-0.5 text-xs rounded-full bg-[var(--color-brand-danger)] text-white", children: item.badge })
        ]
      }
    ),
    hasChildren && /* @__PURE__ */ jsx("div", { className: "mt-1", children: item.children.map((child) => /* @__PURE__ */ jsx(
      SidebarNavItem,
      {
        item: child,
        isActive: false,
        onNavigate,
        level: level + 1
      },
      child.id
    )) })
  ] });
}
function AppSidebar({
  currentApp,
  currentPath = "/",
  onNavigate,
  footer
}) {
  if (!currentApp || !currentApp.navigation) {
    return null;
  }
  return /* @__PURE__ */ jsxs("div", { className: "h-full flex flex-col bg-white", children: [
    /* @__PURE__ */ jsx("nav", { className: "flex-1 overflow-y-auto p-3 space-y-1", children: currentApp.navigation.map((item) => /* @__PURE__ */ jsx(
      SidebarNavItem,
      {
        item,
        isActive: currentPath === item.path,
        onNavigate
      },
      item.id
    )) }),
    footer && /* @__PURE__ */ jsx("div", { className: "flex-shrink-0 p-3 border-t border-[var(--color-neutral-200)]", children: footer })
  ] });
}
AppSidebar.displayName = "AppSidebar";
function AppFooter({
  enableChat = true,
  onChatOpen,
  children,
  copyright,
  links = []
}) {
  const [chatOpen, setChatOpen] = useState(false);
  const handleChatClick = () => {
    setChatOpen(!chatOpen);
    if (!chatOpen) {
      onChatOpen?.();
    }
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs("footer", { className: "h-12 border-t border-[var(--color-neutral-200)] bg-white px-4 flex items-center justify-between", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-4 text-xs text-[var(--color-neutral-600)]", children: [
        copyright && /* @__PURE__ */ jsx("span", { children: copyright }),
        links.length > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx("span", { className: "text-[var(--color-neutral-300)]", children: "|" }),
          links.map((link, index) => /* @__PURE__ */ jsxs("span", { children: [
            /* @__PURE__ */ jsx(
              "a",
              {
                href: link.href,
                className: "hover:text-[var(--color-neutral-900)] transition-colors",
                children: link.label
              }
            ),
            index < links.length - 1 && /* @__PURE__ */ jsx("span", { className: "mx-2 text-[var(--color-neutral-300)]", children: "\xB7" })
          ] }, link.href))
        ] })
      ] }),
      children,
      enableChat && /* @__PURE__ */ jsxs(
        "button",
        {
          type: "button",
          onClick: handleChatClick,
          className: "flex items-center gap-2 px-4 py-2 rounded-[var(--radius-lg)] bg-[var(--color-brand-primary)] text-white hover:opacity-90 transition-opacity",
          children: [
            /* @__PURE__ */ jsx(
              "svg",
              {
                className: "w-4 h-4",
                fill: "none",
                stroke: "currentColor",
                viewBox: "0 0 24 24",
                children: /* @__PURE__ */ jsx(
                  "path",
                  {
                    strokeLinecap: "round",
                    strokeLinejoin: "round",
                    strokeWidth: 2,
                    d: "M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  }
                )
              }
            ),
            /* @__PURE__ */ jsx("span", { className: "text-sm font-medium", children: "Chat with Agent" })
          ]
        }
      )
    ] }),
    chatOpen && /* @__PURE__ */ jsxs("div", { className: "fixed bottom-14 right-4 w-96 h-[500px] bg-white rounded-[var(--radius-lg)] shadow-[var(--shadow-xl)] border border-[var(--color-neutral-200)] flex flex-col z-50", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between p-4 border-b border-[var(--color-neutral-200)]", children: [
        /* @__PURE__ */ jsx("h3", { className: "font-semibold text-[var(--color-neutral-900)]", children: "Chat with Agent" }),
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            onClick: () => setChatOpen(false),
            className: "p-1 rounded hover:bg-[var(--color-neutral-100)] transition-colors",
            children: /* @__PURE__ */ jsx(
              "svg",
              {
                className: "w-5 h-5 text-[var(--color-neutral-600)]",
                fill: "none",
                stroke: "currentColor",
                viewBox: "0 0 24 24",
                children: /* @__PURE__ */ jsx(
                  "path",
                  {
                    strokeLinecap: "round",
                    strokeLinejoin: "round",
                    strokeWidth: 2,
                    d: "M6 18L18 6M6 6l12 12"
                  }
                )
              }
            )
          }
        )
      ] }),
      /* @__PURE__ */ jsx("div", { className: "flex-1 overflow-y-auto p-4", children: /* @__PURE__ */ jsx("div", { className: "text-sm text-[var(--color-neutral-600)] text-center py-8", children: "Chat interface coming soon..." }) }),
      /* @__PURE__ */ jsx("div", { className: "p-4 border-t border-[var(--color-neutral-200)]", children: /* @__PURE__ */ jsxs("div", { className: "flex gap-2", children: [
        /* @__PURE__ */ jsx(
          "input",
          {
            type: "text",
            placeholder: "Type a message...",
            className: "flex-1 px-3 py-2 text-sm rounded-[var(--radius-md)] border border-[var(--color-neutral-300)] focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-primary)] focus:border-transparent"
          }
        ),
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            className: "px-4 py-2 rounded-[var(--radius-md)] bg-[var(--color-brand-primary)] text-white hover:opacity-90 transition-opacity",
            children: /* @__PURE__ */ jsx(
              "svg",
              {
                className: "w-4 h-4",
                fill: "none",
                stroke: "currentColor",
                viewBox: "0 0 24 24",
                children: /* @__PURE__ */ jsx(
                  "path",
                  {
                    strokeLinecap: "round",
                    strokeLinejoin: "round",
                    strokeWidth: 2,
                    d: "M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                  }
                )
              }
            )
          }
        )
      ] }) })
    ] })
  ] });
}
AppFooter.displayName = "AppFooter";
function PersistentShell({
  currentApp,
  availableApps = [],
  user,
  organization: _organization,
  currentPath = "/",
  showHeader = true,
  showSidebar = true,
  showFooter = true,
  enableAppSwitcher = true,
  enableGlobalSearch = true,
  enableNotifications = true,
  enableChat = true,
  logo,
  children,
  onAppSwitch,
  onNavigate,
  onLogout,
  onSearch,
  onChatOpen
}) {
  return /* @__PURE__ */ jsxs("div", { className: "min-h-screen flex flex-col bg-[var(--color-neutral-50)]", children: [
    showHeader && /* @__PURE__ */ jsx(
      AppHeader,
      {
        currentApp,
        availableApps,
        user,
        enableSearch: enableGlobalSearch,
        enableNotifications,
        enableAppSwitcher,
        logo,
        onAppSwitch,
        onLogout,
        onSearch
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: "flex-1 flex overflow-hidden", children: [
      showSidebar && /* @__PURE__ */ jsx("aside", { className: "flex-shrink-0 w-64 border-r border-[var(--color-neutral-200)] bg-[var(--color-neutral-100)] overflow-y-auto", children: /* @__PURE__ */ jsx(
        AppSidebar,
        {
          currentApp,
          currentPath,
          onNavigate
        }
      ) }),
      /* @__PURE__ */ jsx("main", { className: "flex-1 overflow-y-auto", children })
    ] }),
    showFooter && /* @__PURE__ */ jsx(
      AppFooter,
      {
        enableChat,
        onChatOpen,
        copyright: "\xA9 2025 Agent Native Inc.",
        links: [
          { label: "Privacy", href: "/privacy" },
          { label: "Terms", href: "/terms" },
          { label: "Support", href: "/support" }
        ]
      }
    )
  ] });
}
PersistentShell.displayName = "PersistentShell";
function AppSwitcher({
  apps,
  currentAppId,
  onAppSwitch,
  isOpen,
  onClose
}) {
  if (!isOpen) return null;
  const handleAppClick = (appId) => {
    onAppSwitch(appId);
    onClose();
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      "div",
      {
        className: "fixed inset-0 bg-black/20 z-40",
        onClick: onClose,
        "aria-hidden": "true"
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: "fixed top-20 left-1/2 -translate-x-1/2 w-full max-w-2xl bg-white rounded-[var(--radius-lg)] shadow-[var(--shadow-xl)] border border-[var(--color-neutral-200)] z-50 p-6", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between mb-4", children: [
        /* @__PURE__ */ jsx("h2", { className: "text-lg font-semibold text-[var(--color-neutral-900)]", children: "Switch App" }),
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            onClick: onClose,
            className: "p-1 rounded hover:bg-[var(--color-neutral-100)] transition-colors",
            "aria-label": "Close",
            children: /* @__PURE__ */ jsx(
              "svg",
              {
                className: "w-5 h-5 text-[var(--color-neutral-600)]",
                fill: "none",
                stroke: "currentColor",
                viewBox: "0 0 24 24",
                children: /* @__PURE__ */ jsx(
                  "path",
                  {
                    strokeLinecap: "round",
                    strokeLinejoin: "round",
                    strokeWidth: 2,
                    d: "M6 18L18 6M6 6l12 12"
                  }
                )
              }
            )
          }
        )
      ] }),
      /* @__PURE__ */ jsx("div", { className: "grid grid-cols-2 md:grid-cols-3 gap-3 max-h-[400px] overflow-y-auto", children: apps.map((app) => {
        const isActive = app.id === currentAppId;
        return /* @__PURE__ */ jsxs(
          "button",
          {
            type: "button",
            onClick: () => handleAppClick(app.id),
            className: `
                  flex flex-col items-center gap-2 p-4 rounded-[var(--radius-lg)] border-2 transition-all
                  ${isActive ? "border-[var(--color-brand-primary)] bg-blue-50" : "border-[var(--color-neutral-200)] hover:border-[var(--color-neutral-300)] hover:bg-[var(--color-neutral-50)]"}
                `,
            children: [
              /* @__PURE__ */ jsx("div", { className: "text-4xl", children: typeof app.icon === "string" ? app.icon : app.icon }),
              /* @__PURE__ */ jsxs("div", { className: "text-center", children: [
                /* @__PURE__ */ jsx("p", { className: "font-medium text-sm text-[var(--color-neutral-900)]", children: app.name }),
                app.category && /* @__PURE__ */ jsx("p", { className: "text-xs text-[var(--color-neutral-600)] mt-0.5", children: app.category })
              ] }),
              isActive && /* @__PURE__ */ jsx("div", { className: "absolute top-2 right-2", children: /* @__PURE__ */ jsx(
                "svg",
                {
                  className: "w-5 h-5 text-[var(--color-brand-primary)]",
                  fill: "currentColor",
                  viewBox: "0 0 20 20",
                  children: /* @__PURE__ */ jsx(
                    "path",
                    {
                      fillRule: "evenodd",
                      d: "M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z",
                      clipRule: "evenodd"
                    }
                  )
                }
              ) })
            ]
          },
          app.id
        );
      }) }),
      /* @__PURE__ */ jsx("div", { className: "mt-4 pt-4 border-t border-[var(--color-neutral-200)] text-center", children: /* @__PURE__ */ jsx(
        "a",
        {
          href: "/marketplace",
          className: "text-sm text-[var(--color-brand-primary)] hover:underline",
          children: "Browse more apps in the marketplace \u2192"
        }
      ) })
    ] })
  ] });
}
AppSwitcher.displayName = "AppSwitcher";
function MarketplaceMode({ config, children }) {
  const shellContext = useShellContext();
  const shellServices = useShellServices();
  const currentApp = useMemo(() => ({
    id: config.metadata.appId,
    name: config.metadata.name,
    icon: config.metadata.icon,
    version: config.metadata.version,
    description: config.metadata.description,
    navigation: config.navigation?.items,
    category: config.metadata.category
  }), [config]);
  return /* @__PURE__ */ jsx(
    PersistentShell,
    {
      currentApp,
      availableApps: shellContext.availableApps,
      user: shellContext.user,
      organization: shellContext.organization,
      currentPath: shellServices.navigation.getCurrentPath(),
      onAppSwitch: (appId) => shellServices.navigation.navigateToApp(appId),
      onNavigate: (path) => shellServices.navigation.navigateTo(path),
      onLogout: () => {
        console.log("Logout requested");
      },
      onSearch: (query) => {
        console.log("Search:", query);
      },
      onChatOpen: () => {
        shellServices.chat.open();
      },
      children
    }
  );
}
MarketplaceMode.displayName = "MarketplaceMode";
function StandaloneMode({ config: _config, children }) {
  return /* @__PURE__ */ jsx(ThemeProvider, { children: /* @__PURE__ */ jsx("div", { className: "min-h-screen bg-[var(--color-neutral-50)]", children }) });
}
StandaloneMode.displayName = "StandaloneMode";

export { AnalyticsService, AppFooter, AppHeader, AppSidebar, AppSwitcher, ChatService, MarketplaceMode, NavigationService, NotificationService, PersistentShell, ShellAdapter, ShellProvider, StandaloneMode, analyticsService, chatService, detectEnvironment, navigationService, notificationService, useAnalyticsStore, useChatStore, useEnvironment, useNavigationStore, useNotificationStore, useShellContext, useShellServices };
